<template>
  <tbody v-if="this.batch">
    <tr>
      <td style="width: 55px">
        <font-awesome-icon
          v-if="!completedBatch || isRejected"
          :class="iconClass"
          :title="iconTitle"
          :icon="['fas', 'exclamation-triangle']"
        />
      </td>
      <td>
        <div class="is-flex flex-direction--column">
          <div class="is-flex">
            <router-link
              v-if="hasModule('PRSID:MATCH:HISTORY:VIEW')"
              :to="{
                path: isRejected ? `/personId/rejected/${this.batch.id}/details`
                  : `/personId/batch/${batch.id}/details`
              }"
              class="has-text-weight-bold"
            >
              {{ batch.id }}
            </router-link>
            <span v-if="batch.fileName"
              class="padding-left--small has-text-grey max-width-15"
            >
              {{ batch.fileName }}
            </span>
          </div>
          <div class="is-flex has-text-grey">
            {{ batch.submissionTypeCode }}
          </div>
        </div>
      </td>
      <td
        v-if="batch.pidBatchDistrictSchoolAssociation
          && batch.pidBatchDistrictSchoolAssociation.length"
        :class="{'is-hidden-touch': isMobile}"
      >
        <div class="is-flex flex-direction--column">
          <div v-if="isMultiDistrict">
            <b style="font-size:14px">
              Multiple {{ getFieldLabel(`PER:${personType}:ASSIGN:DISTRICT_CODE`) }}(s/es)
            </b>
          </div>
          <div v-else>
            <router-link
              :to="{
                name: 'PIDistrictInfoDetails',
                params: { code: batch.pidBatchDistrictSchoolAssociation[0].districtCode  },
              }"
              class="has-text-weight-bold has-text-primary" style="font-size:14px"
            >
              {{ batch.pidBatchDistrictSchoolAssociation[0].district.location.name }}
            </router-link>
            <span class="padding-left--small has-text-grey is-italic">
              ({{ batch.pidBatchDistrictSchoolAssociation[0].districtCode }})
            </span>
          </div>
          <div v-if="isMultiSchool">
            <span class="is-italic has-text-grey">
              Multiple {{ getFieldLabel(`PER:${personType}:ASSIGN:SCHOOL_CODE`) }}(s/es)
            </span>
          </div>
          <div v-else>
            <router-link
              :to="{
                name: 'PISchoolInfoDetails',
                params: {
                  districtCode: batch.pidBatchDistrictSchoolAssociation[0].districtCode,
                  schoolCode: batch.pidBatchDistrictSchoolAssociation[0].schoolCode,
                },
              }"
              class="is-italic"
            >
              {{ batch.pidBatchDistrictSchoolAssociation[0].school.location.name }}
            </router-link>
            <span class="padding-left--small has-text-grey is-italic">
              ({{ batch.pidBatchDistrictSchoolAssociation[0].schoolCode }})
            </span>
          </div>
        </div>
      </td>
      <td>
        <div class="is-flex flex-direction--column">
          <strong class="is-flex">
            {{
              new Intl.DateTimeFormat('en', {
                year:'numeric',
                month:'2-digit',
                day:'2-digit',
              })
              .format(new Date(batch.createdDate))
            }}
          </strong>
          <span class="has-text-grey">
            {{
              new Intl.DateTimeFormat('en', {
                hour:'numeric',
                minute:'numeric',
              })
              .format(new Date(batch.createdDate))
            }}
          </span>
        </div>
      </td>
      <td v-if="isRejected">
        {{ batch.email }}
      </td>
      <td v-else>
        <div class="has-text-weight-bold">
          {{ recordInfoColumn }} of {{ batch.recordCount }}
        </div>
        <div v-if="!completedBatch" class="has-text-grey">
          PENDING
        </div>
      </td>
      <td :class="{'is-hidden-touch': isMobile}">
        <pid-status-info-icon
          :header="false"
          :processStatus="batch.status"
        />
      </td>
      <td>
        <div class="is-flex flex-justify--end flex-align--center">
          <es-loader v-if="reloading" size="2x"/>
          <router-link
            v-else-if="isRejected"
            class="actions"
            :to="{ path: `/personId/rejected/${this.batch.id}/details` }"
            :title="'View Errors'"
          >
            View Errors
          </router-link>
          <a
            v-else-if="isInternalAction && !action.isHidden"
            @click="handleInternalCall"
            @keypress="handleInternalCall"
            tabindex="0"
            role="button"
            :aria-label="action.text"
            :title="action.text"
            :class="{'is-hidden-mobile': isMobile}"
          >
            {{ action.text }}
          </a>
          <router-link
            v-else-if="!action.isHidden"
            :class="{'is-hidden-mobile': isMobile}"
            :to="{ path: action.link }"
            :title="action.text || 'Actions'"
          >
            {{ action.text }}
          </router-link>
          <a
            v-if="activeMenu.length && !isRejected"
            class="padding-left--1rem"
          >
            <es-menu
              :setValues="activeMenu"
              @callMenuAction="callMenuAction"
            />
          </a>
        </div>
      </td>
    </tr>
  </tbody>
</template>

<script>
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faExclamationTriangle,
} from '@fortawesome/pro-solid-svg-icons';
import PidStatusInfoIcon from '@/components/PidStatusInfoIcon.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsMenu from '@/components/EsMenu.vue';
import PermissionManager from '@/mixins/PermissionManager';
import FieldLabels from '@/mixins/FieldLabels';

library.add(
  faExclamationTriangle,
);

export default {
  mixins: [
    PermissionManager,
    FieldLabels,
  ],
  props: [
    'batchObject',
    'isMobile',
  ],
  components: {
    EsLoader,
    EsMenu,
    PidStatusInfoIcon,
  },
  data() {
    return {
      batch: null,
      reloading: null,
      personType: null,
    };
  },
  created() {
    this.batch = this.batchObject;
    this.loadPidTransactionStatuses();
    this.setDataElements();
  },
  computed: {
    ...mapGetters('lookups', [
      'pidNearMatchesTransGroup',
      'pidCompletedTransGroup',
      'defaultPersonType',
    ]),
    action() {
      // TODO: update links when Near Match page is completed.
      if (['LOADED', 'QUEUED', 'VALIDATION_IN_PROGRESS', 'VALIDATION_COMPLETE',
        'RUNNING_MATCHING', 'ERRORED', 'ERRORED_MATCHING', 'ERRORED_VALIDATION'].includes(this.batch.status)) {
        // this is an internal link to call some function.
        return {
          isInternal: true,
          link: '/personid',
          internalAction: 'refreshBatchRow',
          text: 'Refresh',
          isHidden: !this.hasModule('PRSID:MATCH:HISTORY:VIEW'),
        };
      }
      if (['ERRORED_VALIDATION', 'ERRORED'].includes(this.batch.status)) {
        return {
        //  link: `/personid/batch/${this.batch.id}`,
        //  text: 'Fix Errors',
          isHidden: !this.hasModule('PRSID:MATCH:HISTORY:VIEW'),
        };
      }
      if (['COMPLETED_WITH_PENDING'].includes(this.batch.status)) {
        return {
          link: `/personid/nearMatches?pidBatchId=${this.batch.id}`,
          text: 'Resolve',
          isHidden: !this.hasModule('PRSID:MATCH:RESOLVENM:VIEW'),
        };
      }
      return {
        isHidden: true,
      };
    },
    activeMenu() {
      return this.batchCompletedMenu.filter(item => !item.isHidden);
    },
    batchCompletedMenu() {
      const menuItems = [];
      if (['LOADED', 'QUEUED', 'VALIDATION_IN_PROGRESS', 'VALIDATION_COMPLETE',
        'RUNNING_MATCHING', 'ERRORED', 'ERRORED_MATCHING', 'ERRORED_VALIDATION']
        .includes(this.batch.status) && this.action.text === 'Refresh') {
        menuItems.push({
          id: 0,
          description: 'Refresh',
          isHidden: !this.hasModule('PRSID:MATCH:HISTORY:VIEW'),
          isInternal: true,
          internalAction: 'refreshBatchRow',
        });
      }
      if (this.action.text && ['COMPLETED_WITH_PENDING'].includes(this.batch.status)) {
        menuItems.push({
          id: 1,
          description: 'Resolve',
          path: `/personid/nearMatches?pidBatchId=${this.batch.id}`,
          isHidden: !this.hasModule('PRSID:MATCH:RESOLVENM:VIEW'),
        });
      }
      if (['COMPLETED', 'COMPLETED_WITH_PENDING', 'QUEUED', 'RUNNING_MATCHING', 'VALIDATION_CANCELED'].includes(this.batch.status)) {
        menuItems.push({
          id: 2,
          description: 'Download',
          path: `/personId/batch/${this.batch.id}/download`,
          isHidden: !this.hasModule('PRSID:DWNLD:DOWNLOAD') || !this.isDownloadTypesAllowed,
        });
      }
      menuItems.push({
        id: 3,
        description: 'View Batch Details',
        path: `/personId/batch/${this.batch.id}/details`,
        isHidden: !this.hasModule('PRSID:MATCH:HISTORY:VIEW'),
      });
      return menuItems;
    },
    completedBatch() {
      const { status } = this.batch;
      if (status === 'COMPLETED'
        || status === 'VALIDATION_CANCELED'
        || this.isRejected) {
        return true;
      }
      return false;
    },
    iconClass() {
      return `fa-lg ${this.completedBatch ? 'danger' : 'has-text-warning'}`;
    },
    iconTitle() {
      if (!this.completedBatch) {
        return 'Match Process not yet complete.';
      }
      return 'Rejected';
    },
    isInternalAction() {
      if (this.action.isInternal) {
        return true;
      }
      return false;
    },
    isMultiDistrict() {
      const setOfUniqueDistricts = this.batch.pidBatchDistrictSchoolAssociation
        .map(a => a.districtCode)
        .slice().sort((a, b) => a > b)
        .reduce((a, b) => { if (a.slice(-1)[0] !== b) a.push(b); return a; }, []);
      if (setOfUniqueDistricts.length > 1) return true;
      return false;
    },
    isMultiSchool() {
      const setOfUniqueSchools = this.batch.pidBatchDistrictSchoolAssociation.map(a => a.schoolCode)
        .slice().sort((a, b) => a > b)
        .reduce((a, b) => { if (a.slice(-1)[0] !== b) a.push(b); return a; }, []);
      if (setOfUniqueSchools.length > 1) return true;
      return false;
    },
    isRejected() {
      return this.batch.status === 'REJECTED';
    },
    recordInfoColumn() {
      const statusOfBatch = this.batch.status;
      const pendingStatuses = this.pidNearMatchesTransGroup
        ? this.pidNearMatchesTransGroup.list.map(s => s.id)
        : [];
      const completedStatuses = this.pidCompletedTransGroup
        ? this.pidCompletedTransGroup.list.map(s => s.id)
        : [];
      const assoc = this.batch.pidBatchTransStatusCountAssociation;
      switch (statusOfBatch) {
        case 'COMPLETED_WITH_PENDING': {
          if (Array.isArray(assoc) && assoc.length) {
            const found = assoc.find(({ status }) => pendingStatuses.includes(status));
            return found ? found.statusCount : 0;
          }
          return 0;
        }
        case 'ERRORED_MATCHING':
        case 'RUNNING_MATCHING': {
          if (Array.isArray(assoc) && assoc.length) {
            const otherRecords = assoc
              .map(item => item.statusCount)
              .reduce((prev, curr) => prev + curr, 0);
            const displayRecords = this.batch.recordCount - otherRecords;
            return displayRecords;
          }
          return this.batch.recordCount;
        }
        case 'COMPLETED': {
          if (Array.isArray(assoc) && assoc.length) {
            const l = assoc.filter(({ status }) => completedStatuses.includes(status));
            return l.length ? l.map(item => item.statusCount)
              .reduce((prev, next) => prev + next, 0) : 0;
          }
          return 0;
        }
        default:
          return this.batch.recordCount;
      }
    },
    allowedExtractDownloadTypes() {
      return this.$store.state.propertiesModule.properties['PERSON-EXTRACT_DOWNLOAD_TYPES_ALLOWED'];
    },
    isDownloadTypesAllowed() {
      if (this.allowedExtractDownloadTypes) {
        return this.allowedExtractDownloadTypes.includes(this.batch.submissionTypeCode);
      }
      return null;
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadPidTransactionStatuses',
    ]),
    callMenuAction() {
      this.refreshBatchRow();
    },
    handleInternalCall() {
      this[this.action.internalAction]();
    },
    async refreshBatchRow() {
      try {
        this.reloading = true;
        const res = await axios.get(`personid/batches?ids=${this.batch.id}`);
        [this.batch] = res.data.data;
      } catch (e) {
        // handle error
      }
      this.reloading = false;
    },
    setDataElements() {
      this.personType = this.defaultPersonType;
    },
  },
};
</script>

<style lang="sass" scoped>
  .danger
    color: #DC3545 !important
  .padding-left--small
    padding-left: 0.25rem
  .padding-left--1rem
    padding-left: 1rem
  .max-width-15
    max-width: 20ch
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap
  .actions
    font-weight: bold
    width: 70px !important
</style>
